import { useInfiniteQuery } from '@tanstack/react-query'
import { subDays } from 'date-fns'

import type { ApiResponse, UseEmployeeActivityQueryParams } from './useEmployeeActivityQuery.types'
import { mapInfiniteData } from '@percent/partner-dashboard/helpers/mapInfiniteData'
import { useClient } from '@percent/partner-dashboard/common/hooks/useClient/useClient'
import { calculateRetryDelay, shouldRetryQuery } from '@percent/partner-dashboard/common/hooks'

export const useEmployeeActivityQuery = ({
  accountId,
  pageSize,
  type,
  dateGt,
  dateLt = new Date(),
  timeWindowInDays = 7
}: UseEmployeeActivityQueryParams) => {
  const { gsClient } = useClient()

  return useInfiniteQuery({
    queryKey: [
      'getEmployeeActivity',
      {
        accountId,
        pageSize,
        type
      }
    ] as const,
    initialPageParam: {
      dateLt,
      dateGt: subDays(dateLt, timeWindowInDays),
      nextPageUrl: ''
    },
    queryFn: async ctx => {
      if (ctx.pageParam.nextPageUrl !== '') {
        const response = await gsClient.get<ApiResponse>(ctx.pageParam.nextPageUrl, {
          withCredentials: true
        })

        return response.data
      }

      const response = await gsClient.get<ApiResponse>(
        `v1/partner/giving/employees/${ctx.queryKey[1].accountId}/activity`,
        {
          params: {
            pageSize: ctx.queryKey[1].pageSize,
            type: ctx.queryKey[1].type,
            dateGt: ctx.pageParam.dateGt.toISOString(),
            dateLt: ctx.pageParam.dateLt.toISOString()
          },
          withCredentials: true
        }
      )

      return response.data
    },
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      // eslint-disable-next-line no-underscore-dangle
      const { _links } = lastPage

      if (_links.next) {
        return { ...lastPageParam, nextPageUrl: _links.next }
      }

      const parsedNextItemDate = lastPage.nextItemDate ? new Date(lastPage.nextItemDate) : null

      if (parsedNextItemDate && (!dateGt || parsedNextItemDate > dateGt)) {
        return {
          dateLt: parsedNextItemDate,
          dateGt: subDays(parsedNextItemDate, timeWindowInDays),
          nextPageUrl: ''
        }
      }

      return null
    },
    select: mapInfiniteData,
    retry: (failureCount, error) => shouldRetryQuery(failureCount, error),
    retryDelay: attemptIndex => calculateRetryDelay(attemptIndex)
  })
}
